/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Button, Chip, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InputAdornment from '@mui/material/InputAdornment';
// Pricing page should render standalone without the DashboardLayout wrapper
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession, getAutoToken } from '../../utils/authUtils';
import CheckoutModal from '../Payment/CheckoutModal';
import PaymentService from '../../services/PaymentService';
import PricingTrialCard from '../PricingTrialCard';
import { API_BASE_URL } from '../../config/api';

const PageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '48px',
  background: 'linear-gradient(180deg, #eef8ff 0%, #fffaf6 60%)',
  padding: '20px',
  borderRadius: 16,
  boxShadow: '0 30px 80px rgba(11,145,214,0.08)',
  [theme.breakpoints.down('sm')]: {
    gap: '24px',
    padding: '12px',
    borderRadius: 12
  }
}));

const HeroSection = styled(Box)(({ theme }) => ({
  color: '#ffffff',
  borderRadius: '24px',
  padding: '32px 20px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 48px rgba(11, 145, 214, 0.12)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '16px',
    padding: '20px 12px'
  }
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '36px',
  marginBottom: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    marginBottom: '4px'
  }
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: 1.6,
  maxWidth: '680px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: 1.5
  }
}));

const ToggleGroup = styled(Box)(({ theme }) => ({
  marginTop: '32px',
  display: 'inline-flex',
  backgroundColor: '#ffffff',
  borderRadius: '32px',
  padding: '4px',
  boxShadow: '0 8px 20px rgba(12, 76, 140, 0.12)',
  [theme.breakpoints.down('sm')]: {
    marginTop: '16px',
    borderRadius: '24px'
  }
}));

const ToggleButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'active' })(({ active, theme }) => ({
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  padding: '6px 14px',
  borderRadius: '24px',
  backgroundColor: active ? '#0B91D6' : 'transparent',
  color: active ? '#ffffff' : '#0B6CD6',
  boxShadow: active ? '0 6px 14px rgba(11, 145, 214, 0.12)' : 'none',
  transition: 'all 0.18s ease',
  '&:hover': {
    backgroundColor: active ? '#087BB8' : 'rgba(11, 145, 214, 0.06)'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '5px 12px'
  }
}));

const PlansSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  [theme.breakpoints.down('sm')]: {
    gap: '20px'
  }
}));

const PlansGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '24px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px'
  }
}));

const PlanCard = styled(
  Box,
  {
    shouldForwardProp: (prop) => prop !== 'featured' && prop !== 'selected'
  }
)(({ featured, selected, theme }) => ({
  backgroundColor: featured ? '#ffffff' : '#FFF8F5',
  borderRadius: '18px',
  padding: '32px 28px',
  boxShadow: featured
    ? '0 24px 45px rgba(255,113,26,0.14)'
    : '0 8px 20px rgba(11,145,214,0.04)',
  transform: featured ? 'translateY(-8px)' : 'translateY(0)',
  border: selected ? '2px solid #FF711A' : '1px solid rgba(11,145,214,0.08)',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  position: 'relative',
  minHeight: '420px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible',
  '&:hover': {
    borderColor: '#0B91D6',
    boxShadow: '0 26px 40px rgba(11,145,214,0.10)',
    transform: 'translateY(-12px)'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px 16px',
    borderRadius: '14px',
    minHeight: 'auto',
    transform: 'translateY(0)',
    overflow: 'visible',
    '&:hover': {
      transform: 'translateY(-4px)'
    }
  }
}));

const PopularChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: '-14px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#FF711A',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '12px',
  padding: '0 12px',
  height: '28px',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 0,
    left: 0,
    transform: 'none',
    fontSize: '10px',
    height: '22px',
    padding: '0 8px',
    marginBottom: '8px',
    alignSelf: 'center'
  }
}));

const Price = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '32px',
  fontWeight: 700,
  color: '#0B1D4A',
  [theme.breakpoints.down('sm')]: {
    fontSize: '26px'
  }
}));

const PriceSuffix = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  color: '#6b7280',
  marginTop: '4px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const PlanDescription = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  color: '#4b5563',
  marginTop: '18px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    marginTop: '12px',
    marginBottom: '14px'
  }
}));

const FeatureList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flexGrow: 1,
  [theme.breakpoints.down('sm')]: {
    gap: '10px'
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#1f2a44',
  display: 'flex',
  gap: '10px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    gap: '8px'
  }
}));

const BulletDot = styled('span')(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#FFB88A',
  marginTop: '7px',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '6px',
    height: '6px',
    marginTop: '6px'
  }
}));

const CTAButton = styled(
  Button,
  {
    shouldForwardProp: (prop) => prop !== 'highlighted'
  }
)(({ highlighted, theme }) => ({
  marginTop: '24px',
  textTransform: 'none',
  borderRadius: '10px',
  padding: '12px 20px',
  fontWeight: 700,
  backgroundColor: highlighted ? '#0B91D6' : '#FFF7F3',
  color: highlighted ? '#ffffff' : '#FF711A',
  border: `1px solid ${highlighted ? '#0B91D6' : 'rgba(255,113,26,0.12)'}`,
  boxShadow: highlighted ? '0 18px 40px rgba(11,145,214,0.12)' : 'none',
  '&:hover': {
    backgroundColor: highlighted ? '#087BB8' : '#fff3eb'
  },
  '&:focus-visible': {
    outline: '3px solid rgba(11,145,214,0.12)',
    outlineOffset: '2px'
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '16px',
    padding: '10px 16px',
    fontSize: '14px',
    width: '100%'
  }
}));

// Feature table inside each plan card
const CardFeatureTable = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginTop: '16px',
  padding: '12px',
  backgroundColor: 'rgba(11, 145, 214, 0.03)',
  borderRadius: '10px',
  border: '1px solid rgba(11, 145, 214, 0.08)',
  [theme.breakpoints.down('sm')]: {
    gap: '4px',
    padding: '10px',
    marginTop: '12px',
    borderRadius: '8px'
  }
}));

const CardFeatureRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  borderBottom: '1px solid rgba(11, 145, 214, 0.05)',
  '&:last-child': {
    borderBottom: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '3px 0'
  }
}));

const FeatureLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#4b5563',
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px'
  }
}));

const FeatureValue = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#0B3A66',
  fontWeight: 600,
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px'
  }
}));

const CompareToggleButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #00C6A7 0%, #3B82F6 50%, #8B5CF6 100%)',
  color: '#ffffff',
  borderRadius: 14,
  padding: '10px 18px',
  boxShadow: '0 10px 30px rgba(59,130,246,0.18), inset 0 -2px 8px rgba(255,255,255,0.06)',
  textShadow: '0 1px 0 rgba(0,0,0,0.10)',
  transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 18px 40px rgba(59,130,246,0.20)',
    opacity: 0.98
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    padding: '8px 14px',
    borderRadius: 10
  }
}));

const ComparisonSection = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })(({ open, theme }) => ({
  backgroundColor: open ? '#ffffff' : 'transparent',
  borderRadius: open ? '24px' : 0,
  boxShadow: open ? '0 18px 38px rgba(6, 46, 94, 0.08)' : 'none',
  padding: open ? '32px 32px 12px' : 0,
  overflow: 'hidden',
  transition: 'all 0.28s ease',
  [theme.breakpoints.down('sm')]: {
    borderRadius: open ? '16px' : 0,
    padding: open ? '16px 12px 8px' : 0,
    overflowX: 'auto'
  }
}));

const ComparisonHeader = styled(Box)(({ theme }) => ({
  display: 'grid',
  backgroundColor: '#0B91D6',
  gridTemplateColumns: '2fr repeat(3, 1fr)',
  gap: '18px',
  alignItems: 'center',
  paddingBottom: '18px',
  borderBottom: '1px solid #0B91D6',
  borderRadius: '10px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1.5fr repeat(3, 1fr)',
    gap: '8px',
    paddingBottom: '12px',
    minWidth: '600px'
  }
}));

const ComparisonRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '2fr repeat(3, 1fr)',
  gap: '18px',
  alignItems: 'stretch',
  padding: '14px 0',
  borderBottom: '1px solid #f0f3f9',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1.5fr repeat(3, 1fr)',
    gap: '8px',
    padding: '10px 0',
    minWidth: '600px'
  }
}));

const FeatureCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2a44',
  minHeight: '56px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    minHeight: '44px'
  }
}));

const ComparisonCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  minHeight: '56px',
  padding: '0 8px',
  wordBreak: 'break-word',
  [theme.breakpoints.down('sm')]: {
    minHeight: '44px',
    padding: '0 4px',
    fontSize: '11px'
  }
}));

const planMatrix = {
  monthly: [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for small/local businesses just getting started.',
      priceLabel: '₹1,199',
      priceSuffix: '/ month',
      bullets: [
        'Keyword Research & On-Page SEO',
        'Google Business Profile Setup',
        'Local Directory Submissions',
        'Monthly SEO Report'
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced',
      description: 'Best for businesses ready to scale traffic & visibility.',
      priceLabel: '₹1,999',
      priceSuffix: '/ month',
      featured: true,
      bullets: [
        'Everything in Basic, plus:',
        'Competitor keyword analysis',
        'High-quality backlink building',
        'Bi-weekly growth reports'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'For brands that want to dominate search rankings.',
      priceLabel: '₹2,999',
      priceSuffix: '/ month',
      bullets: [
        'Everything in Advanced, plus:',
        'Multi-location SEO strategy',
        'Content marketing campaigns',
        'Dedicated SEO manager'
      ]
    }
  ],
  annually: [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for small/local businesses just getting started.',
      priceLabel: '₹11,999',
      priceSuffix: '/ year',
      bullets: [
        'Keyword Research & On-Page SEO',
        'Google Business Profile Setup',
        'Local Directory Submissions',
        'Monthly SEO Report'
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced',
      description: 'Best for businesses ready to scale traffic & visibility.',
      priceLabel: '₹19,999',
      priceSuffix: '/ year',
      featured: true,
      bullets: [
        'Everything in Basic, plus:',
        'Competitor keyword analysis',
        'High-quality backlink building',
        'Bi-weekly growth reports'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'For brands that want to dominate search rankings.',
      priceLabel: '₹29,999',
      priceSuffix: '/ year',
      bullets: [
        'Everything in Advanced, plus:',
        'Multi-location SEO strategy',
        'Content marketing campaigns',
        'Dedicated SEO manager'
      ]
    }
  ]
};

// Helper to format rupee amounts to display strings
function formatRupee(amount) {
  if (amount === null || amount === undefined) return '';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

// Compact feature list for each plan card (displayed above Get Started button)
const planCardFeatures = {
  basic: [
    { label: 'Profiles Covered', value: '1 profile' },
    { label: 'Refresh Rate', value: '15 Days' },
    { label: 'Discount on Next Profiles', value: '0%' },
    { label: 'Visibeen Score', value: 'Full dynamic scoring' },
    { label: 'Competitor Tracking', value: '1 competitor' },
    { label: 'Citation Audit', value: '0' },
    { label: 'Keywords Campaigns', value: '1' },
    { label: 'Campaigns', value: '1' },
    { label: 'Website', value: 'Non Sync' },
    { label: 'AI Tasks', value: 'Up to 3' },
    { label: 'Heat Map Access', value: false },
    { label: 'Special Days Boost', value: 'Templates' },
    { label: 'Post Scheduling', value: false },
    { label: 'Directory Listing', value: true },
    { label: 'Geo-Tagged Photos', value: '5' },
    { label: 'Change Notifications', value: false },
    { label: 'Multi-user Access', value: '1 user' },
    { label: 'Support', value: 'Chat' }
  ],
  advanced: [
    { label: 'Profiles Covered', value: '1 profile' },
    { label: 'Refresh Rate', value: '7 Days' },
    { label: 'Discount on Next Profiles', value: '10%' },
    { label: 'Visibeen Score', value: 'Full dynamic scoring' },
    { label: 'Competitor Tracking', value: '3 competitors' },
    { label: 'Citation Audit', value: 'Up to 1/month' },
    { label: 'Keywords Campaigns', value: 'Up to 3' },
    { label: 'Campaigns', value: '2' },
    { label: 'Website', value: 'Auto-Sync' },
    { label: 'AI Tasks', value: 'Up to 20' },
    { label: 'Heat Map Access', value: true },
    { label: 'Special Days Boost', value: 'Templates' },
    { label: 'Post Scheduling', value: '10 posts/month' },
    { label: 'Directory Listing', value: true },
    { label: 'Geo-Tagged Photos', value: '10' },
    { label: 'Change Notifications', value: 'Alerts' },
    { label: 'Multi-user Access', value: '2 users' },
    { label: 'Support', value: 'Chat' }
  ],
  pro: [
    { label: 'Profiles Covered', value: '1 profile' },
    { label: 'Refresh Rate', value: '3 Days' },
    { label: 'Discount on Next Profiles', value: '10%' },
    { label: 'Visibeen Score', value: 'Full dynamic scoring' },
    { label: 'Competitor Tracking', value: '5 competitors' },
    { label: 'Citation Audit', value: 'Up to 2/month' },
    { label: 'Keywords Campaigns', value: 'Up to 10' },
    { label: 'Campaigns', value: '2' },
    { label: 'Website', value: 'Auto-Sync' },
    { label: 'AI Tasks', value: 'Up to 20' },
    { label: 'Heat Map Access', value: true },
    { label: 'Special Days Boost', value: 'Templates/Video' },
    { label: 'Post Scheduling', value: '25 posts + bulk' },
    { label: 'Directory Listing', value: true },
    { label: 'Geo-Tagged Photos', value: '30' },
    { label: 'Change Notifications', value: 'WhatsApp' },
    { label: 'Multi-user Access', value: 'Up to 5 users' },
    { label: 'Support', value: 'Chat + WhatsApp' }
  ]
};

const featureRows = [
  {
    label: 'Profiles Covered',
    values: {
      basic: '1 profile',
      advanced: '1 profile',
      pro: '1 profile'
    }
  },
  {
    label: 'Discount on Next Profiles',
    values: {
      basic: '0%',
      advanced: '10%',
      pro: '10%'
    }
  },
  {
    label: 'Visibeen Score',
    values: {
      basic: 'Full scoring',
      advanced: 'Full scoring + dynamic roadmap',
      pro: 'Full scoring + dynamic roadmap'
    }
  },
  {
    label: 'Competitor Tracking',
    values: {
      basic: '1 competitor',
      advanced: '3 competitors',
      pro: '10 competitors'
    }
  },
  {
    label: 'Citation Audit',
    values: {
      basic: '0',
      advanced: 'Up to 1 per month',
      pro: 'Up to 2 per month'
    }
  },
  {
    label: 'Keywords Campaigns',
    values: {
      basic: '1',
      advanced: 'Up to 3',
      pro: 'Up to 10'
    }
  },
  {
    label: 'Competitor Campaigns',
    values: {
      basic: '1',
      advanced: '2',
      pro: '2'
    }
  },
  {
    label: 'Website',
    values: {
      basic: 'Free One Design',
      advanced: 'Free 3 Design',
      pro: 'Free 3 Design'
    }
  },
  {
    label: 'Review Generation',
    values: {
      basic: false,
      advanced: 'Send links (Email/WhatsApp)',
      pro: 'Send links (Email/WhatsApp)'
    }
  },
  {
    label: 'AI Tasks',
    values: {
      basic: 'Up to 7',
      advanced: 'Up to 20',
      pro: 'Up to 20'
    }
  },
  {
    label: 'Review Insights',
    values: {
      basic: false,
      advanced: 'Basic AI insights',
      pro: 'Basic AI insights'
    }
  },
  {
    label: 'Heat Map Access',
    values: {
      basic: false,
      advanced: 'Yes',
      pro: 'Yes'
    }
  },
  {
    label: 'Radius Optimization',
    values: {
      basic: false,
      advanced: 'Suggested (static)',
      pro: 'Dynamic auto-adjust + competitor unlock'
    }
  },
  {
    label: 'Special Days Boost (NH / Holidays / Events)',
    values: {
      basic: 'Templates',
      advanced: 'Templates',
      pro: { text: 'Templates / Video', tag: '(upcoming)' }
    }
  },
  {
    label: 'Post Scheduling',
    values: {
      basic: false,
      advanced: '10 posts/month',
      pro: 'Up to 25 posts + bulk upload'
    }
  },
  {
    label: 'AI-Powered Tasks',
    values: {
      basic: 'Up to 3',
      advanced: 'Auto captions up to 20',
      pro: 'Full AI post creation + content refresh up to 25'
    }
  },
  {
    label: 'Directory Listing Visibeen',
    values: {
      basic: 'Yes',
      advanced: 'Yes',
      pro: 'Yes'
    }
  },
  {
    label: 'Free Website (SEO ready)',
    values: {
      basic: 'Yes',
      advanced: 'Auto-sync with GMB',
      pro: 'Auto-sync with GMB'
    }
  },
  {
    label: 'Geo-Tagged Photos Upload',
    values: {
      basic: '5',
      advanced: '10',
      pro: '30'
    }
  },
  {
    label: 'Change Notifications',
    values: {
      basic: false,
      advanced: 'Alerts for major changes',
      pro: 'WhatsApp updates for major changes'
    }
  },
  {
    label: 'Multi-user Access',
    values: {
      basic: '1 user',
      advanced: '2 users',
      pro: 'Up to 5 users'
    }
  },
  {
    label: 'Support',
    values: {
      basic: 'Chat',
      advanced: 'Chat',
      pro: 'Chat + WhatsApp updates'
    }
  }
];

const renderComparisonValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        {value.text && (
          <Typography component="span" sx={{ fontSize: '13px', color: '#1f2a44', fontWeight: 500 }}>
            {value.text}
          </Typography>
        )}
        {value.tag && (
          <Typography
            component="span"
            sx={{ fontSize: '11px', fontWeight: 700, color: '#EF4444', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {value.tag}
          </Typography>
        )}
      </Box>
    );
  }

  if (value === false || value === 'Not available') {
    return (
      <Typography component="span" sx={{ fontSize: '18px', fontWeight: 700, color: '#EF4444' }}>
        {'✕'}
      </Typography>
    );
  }

  if (value === true || value === 'Included') {
    return (
      <Typography component="span" sx={{ fontSize: '18px', fontWeight: 700, color: '#16A34A' }}>
        {'✓'}
      </Typography>
    );
  }

  return (
    <Typography component="span" sx={{ fontSize: '13px', color: '#1f2a44', fontWeight: 500 }}>
      {value}
    </Typography>
  );
};

const PlansPricing = () => {
  // Check if user came from ads page - if yes, show both monthly and yearly options
  // If from main portal (no marker), show only yearly plans
  const [showBillingToggle, setShowBillingToggle] = useState(false);

  useEffect(() => {
    let loginSource = null;
    try {
      loginSource = localStorage.getItem('login_source');
    } catch (_) { }

    // If user came from ads page, show both monthly and yearly options
    const fromAdsPage = loginSource === 'ads';
    setShowBillingToggle(fromAdsPage);

    // If not from ads page (main portal), ensure we're on yearly
    if (!fromAdsPage) {
      setBillingCycle('annually');
    }

    console.log('[PlansPricing] login_source=', loginSource, 'showBillingToggle=', fromAdsPage);
  }, []);

  const [billingCycle, setBillingCycle] = useState('annually');
  const [selectedPlan, setSelectedPlan] = useState('advanced');
  const [serverPlans, setServerPlans] = useState(null);

  const navigate = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutProps, setCheckoutProps] = useState({});

  // fallback static matrix is available in `planMatrix`; prefer serverPlans when fetched

  useEffect(() => {
    // Fetch server-side prices so frontend labels match backend/Razorpay
    let mounted = true;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
        const resp = await fetch(`${apiBase}/api/v1/payments/prices`);
        if (!resp.ok) return;
        const json = await resp.json();
        if (!mounted) return;
        if (json && json.success && json.data && Array.isArray(json.data.plans)) {
          const byId = {};
          json.data.plans.forEach(p => {
            byId[p.id] = p;
          });

          const monthly = ['basic', 'advanced', 'pro'].map(id => {
            const p = byId[id] || {};
            return {
              id: id,
              name: p.name || id,
              description: planMatrix.monthly.find(x => x.id === id)?.description || '',
              priceLabel: formatRupee(p.monthly || (planMatrix.monthly.find(x => x.id === id)?.priceLabel && Number(planMatrix.monthly.find(x => x.id === id)?.priceLabel.replace(/[^0-9]/g, '')))),
              priceSuffix: '/ month',
              bullets: planMatrix.monthly.find(x => x.id === id)?.bullets || [],
              featured: p.featured || false
            };
          });

          const annually = ['basic', 'advanced', 'pro'].map(id => {
            const p = byId[id] || {};
            return {
              id: id,
              name: p.name || id,
              description: planMatrix.annually.find(x => x.id === id)?.description || '',
              priceLabel: formatRupee(p.annually || (planMatrix.annually.find(x => x.id === id)?.priceLabel && Number(planMatrix.annually.find(x => x.id === id)?.priceLabel.replace(/[^0-9]/g, '')))),
              priceSuffix: '/ year',
              bullets: planMatrix.annually.find(x => x.id === id)?.bullets || [],
              featured: p.featured || false
            };
          });

          setServerPlans({ monthly, annually });
        }
      } catch (err) {
        console.warn('Failed to fetch server prices', err.message || err);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const plansSource = serverPlans ? serverPlans[billingCycle] : planMatrix[billingCycle];

  // Debug: log when server prices or billing cycle change to help trace mismatches
  useEffect(() => {
    try {
      // eslint-disable-next-line no-console
      console.log('[PlansPricing] billingCycle=', billingCycle, 'serverPlansLoaded=', !!serverPlans, 'plansSource=', plansSource.map?.(p => ({ id: p.id, price: p.priceLabel })));
    } catch (e) { }
  }, [serverPlans, billingCycle]);

  const [compareOpen, setCompareOpen] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialModalCycle, setTrialModalCycle] = useState(billingCycle);
  const [trialModalSelected, setTrialModalSelected] = useState('basic');
  const location = useLocation();
  // Coupon logic moved to CheckoutModal — users should apply trial and discount coupons during checkout
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // Always hide the special ₹1 trial card — pricing is same for everyone
  const showTrialCard = false;
  // Trial flow disabled — provide lightweight fallbacks to satisfy legacy references
  const isTrialMode = false;
  const serverTrial = { trialAmount: 1 };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const handleGetStarted = (planId) => {
    // Open checkout modal; coupon/trial should be applied inside checkout
    setCheckoutProps({ planId, billingCycle, amount: null, isTrial: false });
    setCheckoutOpen(true);
  };

  const startInlineTrial = (planId) => {
    setCheckoutProps({ planId, billingCycle, amount: 1, isTrial: true });
    setCheckoutOpen(true);
  };

  // Handler for trial or choose actions from the trial card
  const plansRef = useRef(null);
  const handleChoosePlan = (planCode, opts = {}) => {
    if (opts.trial) {
      // Open inline trial selection modal instead of redirecting
      setTrialModalSelected(planCode || 'basic');
      setTrialModalCycle(billingCycle);
      setTrialModalOpen(true);
      return;
    }

    if (isTrialMode) {
      // User is in trial flow via query param — open checkout modal in trial mode
      setSelectedPlan(planCode);
      setCheckoutProps({ planId: planCode, billingCycle, amount: 1, isTrial: true });
      setCheckoutOpen(true);
      return;
    }

    // Normal choose flow: open regular checkout
    setCheckoutProps({ planId: planCode, billingCycle, amount: null });
    setCheckoutOpen(true);
  };

  return (
    <>
      <Box sx={{ width: '100%', px: { xs: 2, sm: 4, md: 8 }, py: { xs: 2, sm: 4, md: 6 } }}>
        <PageWrapper>

          <PlansSection>
            {/* Coupon input removed from Plans page — apply coupons during checkout */}
            {/* Trial card placed above plans */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              {showTrialCard && (
                <PricingTrialCard
                  planCode="basic"
                  price={`₹${serverTrial.trialAmount}`}
                  features={['Dashboard access', 'Review replies', 'Create & schedule posts']}
                  onChoosePlan={handleChoosePlan}
                  large={true}
                />
              )}
            </Box>

            {/* Billing toggle - shown only for users from ads page, main portal users see yearly only */}
            {showBillingToggle && (
              <Box sx={{ display: 'inline-flex', justifyContent: 'center', mb: 3 }}>
                <ToggleGroup>
                  <ToggleButton active={billingCycle === 'monthly'} onClick={() => setBillingCycle('monthly')}>
                    Monthly
                  </ToggleButton>
                  <ToggleButton active={billingCycle === 'annually'} onClick={() => setBillingCycle('annually')}>
                    Annually
                  </ToggleButton>
                </ToggleGroup>
              </Box>
            )}

            <PlansGrid ref={plansRef}>
              {plansSource.map((plan) => {
                const isSelected = selectedPlan === plan.id;
                const displayPrice = plan.priceLabel;
                const displaySuffix = plan.priceSuffix;
                return (
                  <PlanCard
                    key={plan.id}
                    featured={plan.featured}
                    selected={isSelected}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {plan.featured && (
                      <PopularChip icon={<StarIcon fontSize="small" />} label="Most Popular" />
                    )}

                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography fontSize="13px" fontWeight={700} color="#111827" textTransform="uppercase">
                        {plan.name}
                      </Typography>
                      <Price sx={{ mt: 1 }}>{displayPrice}</Price>
                      <PriceSuffix>{displaySuffix}</PriceSuffix>
                    </Box>

                    {/* Feature comparison table inside each card */}
                    <CardFeatureTable>
                      {planCardFeatures[plan.id]?.map((feature, idx) => (
                        <CardFeatureRow key={idx}>
                          <FeatureLabel>{feature.label}</FeatureLabel>
                          <FeatureValue>
                            {feature.value === true ? (
                              <Typography component="span" sx={{ color: '#16A34A', fontWeight: 700 }}>✓</Typography>
                            ) : feature.value === false ? (
                              <Typography component="span" sx={{ color: '#EF4444', fontWeight: 700 }}>✕</Typography>
                            ) : (
                              feature.value
                            )}
                          </FeatureValue>
                        </CardFeatureRow>
                      ))}
                    </CardFeatureTable>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                      <CTAButton
                        highlighted={isSelected}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectPlan(plan.id);
                          handleGetStarted(plan.id);
                        }}
                      >
                        Get Started
                      </CTAButton>

                      {/* Inline trial button removed per request */}
                    </Box>

                  </PlanCard>
                );
              })}
            </PlansGrid>
          </PlansSection>
        </PageWrapper>
      </Box>
      {/* Trial Selection Modal */}
      <Dialog open={trialModalOpen} onClose={() => setTrialModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: -3, mb: 2 }}>
            <ToggleGroup>
              <ToggleButton active={trialModalCycle === 'monthly'} onClick={() => setTrialModalCycle('monthly')}>Monthly</ToggleButton>
              <ToggleButton active={trialModalCycle === 'annually'} onClick={() => setTrialModalCycle('annually')}>Annually</ToggleButton>
            </ToggleGroup>
          </Box>



          <PlansGrid>
            {(serverPlans ? serverPlans[trialModalCycle] : planMatrix[trialModalCycle]).map((plan) => {
              const isSelected = trialModalSelected === plan.id;
              return (
                <PlanCard key={plan.id} featured={plan.featured} selected={isSelected} onClick={() => setTrialModalSelected(plan.id)}>
                  {plan.featured && <PopularChip icon={<StarIcon fontSize="small" />} label="Most Popular" />}
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography fontSize="13px" fontWeight={700} color="#111827" textTransform="uppercase">{plan.name}</Typography>
                    <Price sx={{ mt: 1 }}>{plan.priceLabel}</Price>
                    <PriceSuffix>{plan.priceSuffix}</PriceSuffix>
                  </Box>
                  {/* Feature comparison table inside trial modal cards */}
                  <CardFeatureTable>
                    {planCardFeatures[plan.id]?.map((feature, idx) => (
                      <CardFeatureRow key={idx}>
                        <FeatureLabel>{feature.label}</FeatureLabel>
                        <FeatureValue>
                          {feature.value === true ? (
                            <Typography component="span" sx={{ color: '#16A34A', fontWeight: 700 }}>✓</Typography>
                          ) : feature.value === false ? (
                            <Typography component="span" sx={{ color: '#EF4444', fontWeight: 700 }}>✕</Typography>
                          ) : (
                            feature.value
                          )}
                        </FeatureValue>
                      </CardFeatureRow>
                    ))}
                  </CardFeatureTable>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                    <CTAButton highlighted={isSelected} onClick={() => {
                      // Open checkout in trial mode for selected plan
                      setCheckoutProps({ planId: plan.id, billingCycle: trialModalCycle, amount: 1, isTrial: true });
                      setCheckoutOpen(true);
                      setTrialModalOpen(false);
                    }}>Start 7‑Day Trial</CTAButton>
                  </Box>
                </PlanCard>
              );
            })}
          </PlansGrid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTrialModalOpen(false)} variant="outlined" sx={{
            textTransform: 'none',
            borderRadius: 8,
            color: '#374151',
            borderColor: '#e6eef8',
            px: 3,
            '&:hover': { backgroundColor: '#f5f7fa' }
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <CheckoutModal
        open={checkoutOpen}
        onClose={(result) => {
          setCheckoutOpen(false);
          if (result?.success) {
            // Clear login_source marker after successful payment
            try { localStorage.removeItem('login_source'); } catch (_) { }
            // handle success (e.g., show toast) — for now console.log
            console.log('Checkout success', result.result);
          }
        }}
        planId={checkoutProps.planId}
        billingCycle={checkoutProps.billingCycle}
        amount={checkoutProps.amount}
        isTrial={checkoutProps.isTrial}
      />
    </>
  );
};

export default PlansPricing;
